<template>
  <Space direction="vertical" style="width: 100%">
    <div class="info-container" :class="bannerClass">
      <div class="icon">
        <LoadingOutlined
          v-if="phase === 'pending'"
          spin
          style="font-size: 48px; color: #2563eb"
        />
        <IconFont
          v-else-if="phase === 'success'"
          type="icon-wancheng"
          style="font-size: 50px"
        />
        <CloseCircleFilled
          v-else
          style="font-size: 48px; color: #dc2626"
        />
      </div>
      <div class="info">
        <div class="top">
          <span class="headline">{{ headlineText }}</span>
          <span class="thank-text" v-if="phase === 'success'">
            感谢您选择铁路出行！您的改签订单号为：
          </span>
          <span class="order-text" v-if="query.orderSn">{{ query.orderSn }}</span>
        </div>
        <div v-if="phase === 'pending'" class="tip-text">
          改签申请已提交，正在等待出票结果，请稍候…
        </div>
        <div v-else-if="phase === 'success'" class="tip-text">
          {{
            userInfo?.realName
          }}
          先生/女士可持购票时所使用的有效身份证件原件，于列车开车前到车站检票乘车（电子客票无需换取纸质车票）。
        </div>
        <div v-else class="tip-text fail-tip">
          {{ failMessage || '改签出票失败，请前往订单列表查看或重新办理改签。' }}
        </div>
      </div>
    </div>

    <div class="custom">
      <Card title="改签订单信息" v-if="newOrderDetail">
        <div class="ticket-info">
          <span class="main-text"
            >{{ newOrderDetail.ridingDate }}（{{
              getWeekNumber(dayjs(newOrderDetail.ridingDate).day())
            }}）{{ newOrderDetail.trainNumber }}</span
          >
          <span class="small-text">次</span>
          <span class="main-text">{{ newOrderDetail.departure }}</span>
          <span class="small-text">站</span>
          <span class="main-text"
            >（{{ newOrderDetail.departureTime }}开）-{{
              newOrderDetail.arrival
            }}</span
          >
          <span class="small-text"
            >站（{{ newOrderDetail.arrivalTime }}到）</span
          >
        </div>
        <Table
          :columns="passengerColumns"
          size="small"
          :data-source="newOrderDetail.passengerDetails"
          :pagination="false"
        />
        <Divider v-if="phase === 'success'" />
        <Space
          v-if="phase === 'success'"
          style="justify-content: center; width: 100%"
        >
          <Button :disabled="true">餐饮·特产</Button>
          <Button @click="router.push('/ticketSearch')">继续购票</Button>
          <Button @click="goOrderList">查看订单详情</Button>
        </Space>
      </Card>
    </div>

    <Card
      v-if="originOrderDetail"
      title="原订单信息"
      class="origin-order-card"
    >
      <div class="ticket-info">
        <span class="main-text"
          >{{ originOrderDetail.ridingDate }}（{{
            getWeekNumber(dayjs(originOrderDetail.ridingDate).day())
          }}）{{ originOrderDetail.trainNumber }}</span
        >
        <span class="small-text">次</span>
        <span class="main-text">{{ originOrderDetail.departure }}</span>
        <span class="small-text">站</span>
        <span class="main-text"
          >（{{ originOrderDetail.departureTime }}开）-{{
            originOrderDetail.arrival
          }}</span
        >
        <span class="small-text"
          >站（{{ originOrderDetail.arrivalTime }}到）</span
        >
      </div>
      <Table
        :columns="originColumns"
        size="small"
        :data-source="originOrderDetail.passengerDetails"
        :pagination="false"
      />
    </Card>
  </Space>
</template>

<script setup>
import {
  Card,
  Space,
  Table,
  Divider,
  Button
} from 'ant-design-vue'
import {
  LoadingOutlined,
  CloseCircleFilled
} from '@ant-design/icons-vue'
import IconFont from '@/components/icon-font'
import { fetchOrderBySn, fechUserInfo } from '@/service'
import { computed, h, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Cookie from 'js-cookie'
import dayjs from 'dayjs'
import {
  ID_CARD_TYPE,
  TICKET_TYPE_LIST,
  SEAT_CLASS_TYPE_LIST,
  TICKET_STATUS_LIST
} from '@/constants'
import { getWeekNumber } from '@/utils'

const { query } = useRoute()
const router = useRouter()

const newOrderDetail = ref(null)
const originOrderDetail = ref(null)
const userInfo = ref(null)
const phase = ref('pending')
const failMessage = ref('')

let timer = null
let pollCount = 0
const MAX_POLL = 120

/** 乘客明细状态（优先接口 statusName，否则按 status 映射） */
const passengerStatusLabel = (record) => {
  if (!record) return '--'
  if (record.statusName) return record.statusName
  return (
    TICKET_STATUS_LIST.find((item) => item.value === record.status)?.label ?? '--'
  )
}

const buildPassengerColumns = () => [
  {
    title: '序号',
    customRender: ({ index }) => h('div', { innerHTML: index + 1 })
  },
  { title: '姓名', dataIndex: 'realName' },
  {
    title: '证件类型',
    dataIndex: 'idType',
    customRender: ({ text }) =>
      h('div', {
        innerHTML: ID_CARD_TYPE.find((item) => item.value === text)?.label
      })
  },
  { title: '证件号码', dataIndex: 'idCard' },
  {
    title: '乘客类型',
    dataIndex: 'ticketType',
    customRender: ({ text }) =>
      h('div', {
        innerHTML: TICKET_TYPE_LIST.find((item) => item.value === text)?.label
      })
  },
  {
    title: '席别',
    dataIndex: 'seatType',
    customRender: ({ text }) =>
      h('div', {
        innerHTML: SEAT_CLASS_TYPE_LIST.find((item) => item.code === text)?.label
      })
  },
  {
    title: '车厢',
    dataIndex: 'carriageNumber',
    customRender: ({ text }) =>
      h('div', { innerHTML: text ? `${text}车` : '—' })
  },
  {
    title: '席位号',
    dataIndex: 'seatNumber',
    customRender: ({ text }) => h('div', { innerHTML: text || '—' })
  },
  {
    title: '票价（元）',
    dataIndex: 'amount',
    customRender: ({ text }) =>
      h('a', { innerHTML: text != null ? `${text / 100}¥` : '—' })
  },
  {
    title: '车票状态',
    customRender: ({ record }) =>
      h('div', { innerHTML: passengerStatusLabel(record) })
  }
]

const passengerColumns = computed(() => buildPassengerColumns())

const originColumns = computed(() => buildPassengerColumns())

const headlineText = computed(() => {
  if (phase.value === 'pending') return '改签中，请稍候…'
  if (phase.value === 'success') return '改签成功！'
  return '改签未完成'
})

const bannerClass = computed(() => ({
  'banner-pending': phase.value === 'pending',
  'banner-success': phase.value === 'success',
  'banner-failed': phase.value === 'failed'
}))

const goOrderList = () => {
  const orderSn =
    newOrderDetail.value?.orderSn || query?.orderSn
  router.push({
    path: '/ticketList',
    query: orderSn ? { tab: '1', orderSn: String(orderSn) } : { tab: '1' }
  })
}

const isRescheduleDone = (newOrder, originOrder) => {
  if (!newOrder) return false
  const ns = newOrder.status
  if (ns === 22 || ns === 3) return true
  if (originOrder) {
    const os = originOrder.status
    if (os === 32 || os === 34) return true
  }
  return false
}

const isRescheduleFailed = (newOrder, originOrder) => {
  if (!newOrder) return false
  const ns = newOrder.status
  if (ns === 23 || ns === 5) return true
  if (originOrder?.status === 33) return true
  return false
}

const refreshOrders = async () => {
  if (!query.orderSn) return
  const [newRes, originRes] = await Promise.all([
    fetchOrderBySn({ orderSn: query.orderSn }),
    query.originOrderSn
      ? fetchOrderBySn({ orderSn: query.originOrderSn })
      : Promise.resolve(null)
  ])
  if (newRes?.success) newOrderDetail.value = newRes.data
  if (originRes?.success) originOrderDetail.value = originRes.data

  if (isRescheduleFailed(newOrderDetail.value, originOrderDetail.value)) {
    phase.value = 'failed'
    stopPolling()
    return
  }
  if (isRescheduleDone(newOrderDetail.value, originOrderDetail.value)) {
    phase.value = 'success'
    stopPolling()
  }
}

const stopPolling = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

const startPolling = () => {
  stopPolling()
  timer = setInterval(async () => {
    pollCount += 1
    await refreshOrders()
    if (pollCount >= MAX_POLL && phase.value === 'pending') {
      phase.value = 'failed'
      failMessage.value = '改签结果等待超时，请稍后在订单列表中查看状态。'
      stopPolling()
    }
  }, 1500)
}

onMounted(async () => {
  if (!query.orderSn) {
    router.replace('/ticketList')
    return
  }
  fechUserInfo({ username: Cookie.get('username') }).then((res) => {
    userInfo.value = res?.data
  })
  await refreshOrders()
  if (phase.value === 'pending') {
    startPolling()
  }
})

onUnmounted(() => {
  stopPolling()
})
</script>

<style lang="scss" scoped>
.info-container {
  width: 100%;
  padding: 20px 40px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  box-shadow: 0 10px 26px rgba(37, 99, 235, 0.08);

  .icon {
    margin-right: 30px;
  }

  .info {
    .top {
      margin-bottom: 6px;

      .headline {
        font-size: 18px;
        font-weight: bolder;
        margin-right: 8px;
      }

      .thank-text {
        font-size: 14px;
        color: #0f172a;
      }

      .order-text {
        font-size: 16px;
        color: #ea580c;
        font-weight: bolder;
      }
    }

    .tip-text {
      color: rgba(0, 0, 0, 0.6);
      font-size: 14px;
    }

    .fail-tip {
      color: #b91c1c;
    }
  }
}

.banner-pending {
  border: 1px solid #bfdbfe;
  background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%);

  .headline {
    color: #1d4ed8;
  }
}

.banner-success {
  border: 1px solid #c9e6cc;
  background: linear-gradient(180deg, #f4fff0 0%, #ecfce8 100%);
  box-shadow: 0 10px 26px rgba(34, 197, 94, 0.12);

  .headline {
    color: #16a34a;
  }
}

.banner-failed {
  border: 1px solid #fecaca;
  background: linear-gradient(180deg, #fef2f2 0%, #fee2e2 100%);

  .headline {
    color: #dc2626;
  }
}

.ticket-info {
  margin-bottom: 10px;
  padding: 10px 12px;
  border: 1px solid #e5edf8;
  border-radius: 10px;
  background: #f8fbff;

  .main-text {
    font-size: 16px;
    font-weight: bolder;
    color: #0f172a;
  }

  .small-text {
    font-size: 14px;
    color: #64748b;
  }
}

.origin-order-card {
  margin-top: 4px;
  border-color: #e2e8f0;
  background: #fafbfc;
}

::v-deep {
  .ant-table-thead {
    .ant-table-cell {
      background: #f3f3f3 !important;
      background-color: #f3f3f3 !important;
    }
  }

  .ant-card {
    border-radius: 14px;
  }
}
</style>
